import { createPropertySelectors, createSelector } from '@ngxs/store';
import _ from 'lodash';

import { AppStateModel } from './app.state.model';
import { AppState } from './app.state';
import { AppStateHelper } from 'src/app/store/app/app.state.helper';
import { DeviceStatus } from 'src/app/models';
import { PageConstant, PosFeatureFlag } from 'src/app/constants';

export class AppSelectors {
  static slices = createPropertySelectors<AppStateModel>(AppState);

  static facility_details = createSelector(
    [AppSelectors.slices.facility_details],
    (facility_details) => facility_details
  );

  static facilityTimeZone = createSelector(
    [AppSelectors.slices.facility_details],
    (facility_details) => facility_details?.time_zone
  );

  static getCreditCardFeeEnabled = createSelector(
    [AppSelectors.facility_details],
    (facility_details) => facility_details?.credit_card_fee_enabled || false
  );

  static getPennyRoundingEnabled = createSelector(
    [AppSelectors.facility_details],
    (facility_details) => facility_details?.penny_rounding_enabled || false
  );

  static getOperator = createSelector(
    [AppSelectors.slices.operator],
    (operator) => operator
  );

  static getOperatorId = createSelector(
    [AppSelectors.getOperator],
    (operator) => operator.id
  );

  static getOperatorPermissions = createSelector(
    [AppSelectors.getOperator],
    (operator) => operator.permissions
  );

  static getOperatorDiscountPermission = createSelector(
    [AppSelectors.getOperatorPermissions],
    (permissions) => permissions.discount
  );

  static getIsInputFocused = createSelector(
    [AppSelectors.slices.isInputFocused],
    (isInputFocused) => isInputFocused
  );

  static device = createSelector(
    [AppSelectors.slices.device],
    (device) => device
  );

  static payment_location = createSelector(
    [AppSelectors.slices.payment_location],
    (payment_location) => payment_location
  );

  static printers = createSelector(
    [AppSelectors.slices.printers],
    (printers) => printers
  );

  static idleSetup = createSelector(
    [AppSelectors.slices.idleSetup],
    (idleSetup) => idleSetup
  );

  static isInputFocused = createSelector(
    [AppSelectors.slices.isInputFocused],
    (isInputFocused) => isInputFocused
  );

  static tempOperator = createSelector(
    [AppSelectors.slices.tempOperator],
    (tempOperator) => tempOperator
  );

  static visiting_facilities = createSelector(
    [AppSelectors.slices.visiting_facilities],
    (visiting_facilities) => visiting_facilities
  );

  private static apiBaseUrl = createSelector(
    [AppSelectors.slices.apiBaseUrl],
    (apiBaseUrl) => apiBaseUrl
  );

  // move from app.selectors.ts
  static getApiUrl = createSelector([AppSelectors.apiBaseUrl], (apiBaseUrl) =>
    AppStateHelper.getApiUrl(apiBaseUrl)
  );

  // move from app.selectors.ts
  static getPosManagerApiUrl = createSelector(
    [AppSelectors.apiBaseUrl],
    (apiBaseUrl) => AppStateHelper.getPosManagerApiUrl(apiBaseUrl)
  );

  // move from app.selectors.ts
  // static getToken = createSelector(
  //   [AppSelectors.device],
  //   ({ token }) => token
  // );

  // move from app.selectors.ts
  static getDeviceKey = createSelector([AppSelectors.device], ({ key }) => key);

  // move from app.selectors.ts
  static getPosManagerToken = createSelector(
    [AppSelectors.getOperator],
    (operator) => operator?.manager_access_jwt
  );

  // move from app.selectors.ts
  static hasPosManagerPermission = createSelector(
    [AppSelectors.getPosManagerToken],
    (token) => !!token
  );

  // move from app.selectors.ts
  static isDeviceRegistered = createSelector(
    [AppSelectors.device],
    (device) => device?.status === DeviceStatus.Active
  );

  // move from app.selectors.ts
  static getOperators = createSelector(
    [AppSelectors.facility_details],
    (facility_details) => facility_details?.operators || []
  );

  // move from app.selectors.ts
  static getCardTerminals = createSelector(
    [AppSelectors.facility_details],
    (facility_details) => facility_details?.card_terminals || []
  );

  // move from app.selectors.ts
  private static defaultPrinter = createSelector(
    [AppSelectors.slices.defaultPrinter],
    (defaultPrinter) => defaultPrinter
  );

  // move from app.selectors.ts
  static getDefaultPrinter = createSelector(
    [
      AppSelectors.defaultPrinter,
      AppSelectors.printers,
      AppSelectors.payment_location
    ],
    (defaultPrinter, printers, payment_location) =>
      AppStateHelper.getDefaultPrinter(
        defaultPrinter,
        printers,
        payment_location?.default_printer_id
      )
  );

  // move from app.selectors.ts
  static getDefaultCardTerminal = createSelector(
    [AppSelectors.getCardTerminals, AppSelectors.device],
    (card_terminals, device) =>
      AppStateHelper.getDefaultCardTerminal(
        card_terminals,
        device?.default_payment_terminal_id
      )
  );

  private static facilityDiscounts = createSelector(
    [AppSelectors.facility_details],
    (facility_details) => facility_details?.discounts || []
  );

  // move from app.selectors.ts
  static getDiscounts = createSelector(
    [AppSelectors.facilityDiscounts],
    (facilityDiscounts) => _.sortBy(facilityDiscounts, ['sort_order'])
  );

  // move from app.selectors.ts
  static isAllowTransferLocation = createSelector(
    [AppSelectors.device],
    ({ allow_pos_location_transfer }) => allow_pos_location_transfer
  );

  static tenant = createSelector(
    [AppSelectors.device],
    (device) => device.tenant
  );

  static enable_pos_beta_reports = createSelector(
    [AppSelectors.facility_details],
    (facility_details) => !!facility_details?.enable_pos_beta_reports
  );

  static isSuperDevice = createSelector(
    [AppSelectors.device],
    ({ super_device }) => super_device
  );
  static currentTenant = createSelector(
    [AppSelectors.device],
    ({ tenant }) => tenant
  );

  static hardwareId = createSelector(
    [AppSelectors.device],
    ({ hardware_id }) => hardware_id
  );

  static currentFacility = createSelector(
    [AppSelectors.device],
    ({ facility_id }) => facility_id
  );

  static networkConnected = createSelector(
    [AppSelectors.slices.networkConnected],
    (networkConnected) => networkConnected
  );

  static lastTicketTransaction = createSelector(
    [AppSelectors.slices.lastTicketTransaction],
    (lastTicketTransaction) => lastTicketTransaction
  );

  static hasLastTicketTransaction = createSelector(
    [AppSelectors.lastTicketTransaction],
    (lastTicketTransaction) => !!lastTicketTransaction
  );

  static trainingMode = createSelector(
    [AppSelectors.slices.trainingMode],
    (trainingMode) => !!trainingMode
  );

  static trainingEnabled = createSelector(
    [AppSelectors.facility_details, AppSelectors.slices.trainingMode],
    (facility_details, trainingMode) =>
      !!facility_details?.training_enabled || !!trainingMode
  );

  static trainingModeFacilityMismatch = createSelector(
    [AppSelectors.slices.trainingModeFacilityMismatch],
    (mismatch) => !!mismatch
  );

  // TOUCH-496 POS sub-license gates. Fail-open: absent/undefined ⇒ enabled;
  // hide only on an explicit `false` from the authenticate payload.
  static reservationsEnabled = createSelector(
    [AppSelectors.facility_details],
    (facility_details) => facility_details?.reservations_enabled !== false
  );

  static extraServicesEnabled = createSelector(
    [AppSelectors.facility_details],
    (facility_details) => facility_details?.extra_services_enabled !== false
  );

  // POS-2028: generic enabled-flags list from the authenticate response
  // (Facility#enabled_feature_flags) — the single source of truth for which
  // flags are on for this facility. Check membership by name rather than
  // adding a bespoke boolean per flag.
  static enabledFeatureFlags = createSelector(
    [AppSelectors.facility_details],
    (facility_details) =>
      facility_details?.enabled_feature_flags?.map((flag) => flag.name) || []
  );

  static voidReasonSelectionEnabled = createSelector(
    [AppSelectors.enabledFeatureFlags],
    (flags) => flags.includes('touch_void_reason_selection')
  );

  static refundReasonSelectionEnabled = createSelector(
    [AppSelectors.enabledFeatureFlags],
    (flags) => flags.includes('touch_refund_reason_selection')
  );

  // POS-1238. Fail-closed: the flag must be present in the facility's
  // enabled_feature_flags from the register_device response.
  static modifierPromptSortOrderEnabled = createSelector(
    [AppSelectors.enabledFeatureFlags],
    (flags) => flags.includes(PosFeatureFlag.MODIFIER_PROMPT_SORT_ORDER)
  );

  // POS-1927. Fail-closed: without the flag the device keeps checking PINs
  // locally, exactly as before.
  static serverSideOperatorAuthEnabled = createSelector(
    [AppSelectors.enabledFeatureFlags],
    (flags) => flags.includes(PosFeatureFlag.SERVER_SIDE_OPERATOR_AUTH)
  );

  // Root page for a Services revenue center: booking is license-gated, so fall
  // back to Shop (the only tab left) when Extra Services is off.
  static servicesRootPage = createSelector(
    [AppSelectors.extraServicesEnabled],
    (enabled) =>
      enabled
        ? PageConstant.SERVICES_BOOKING_PAGE
        : PageConstant.SERVICES_SHOP_PAGE
  );

  //#region Multilingual

  static getLocales = createSelector(
    [AppSelectors.slices.locales],
    (locales) => locales ?? []
  );

  static getPrimaryLocale = createSelector(
    [AppSelectors.getLocales],
    (locales) => locales.find((locale) => locale.primary)
  );

  static getSelectedLocale = createSelector(
    [AppSelectors.slices.selectedLocale],
    (selectedLocale) => selectedLocale
  );

  static getSelectedLocaleCode = createSelector(
    [AppSelectors.getSelectedLocale],
    (selectedLocale) => selectedLocale?.code
  );

  static getEnabledFeatureFlags = createSelector(
    [AppSelectors.slices.enabled_feature_flags],
    (flags) => flags ?? []
  );

  static isMultilingualApiEnabled = createSelector(
    [AppSelectors.getEnabledFeatureFlags],
    (flags) => flags.some((flag) => flag.name === 'pos_multilingual_api')
  );

  static isLanguageSwitcherEnabled = createSelector(
    [AppSelectors.getEnabledFeatureFlags],
    (flags) => flags.some((flag) => flag.name === 'pos_language_switcher')
  );

  //#endregion
}
